
import { useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { getPhotos, getPhotoUrl } from "@/services/photoService";
import { ImageOff, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useReveal } from "@/hooks/useReveal";

const CATEGORY_LABELS: Record<string, string> = {
  "intérieur": "Intérieur",
  "extérieur": "Extérieur",
  "chambre": "Chambres",
  "salle de bain": "Salles de bain",
  "piscine": "Piscine",
  "cuisine": "Cuisine",
  "salon": "Salon",
  "terrasse": "Terrasse",
};

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [lightboxPhoto, setLightboxPhoto] = useState<{ url: string; title: string } | null>(null);
  const headerReveal = useReveal<HTMLDivElement>();

  const { data: photos, isLoading, error } = useQuery({
    queryKey: ["photos"],
    queryFn: getPhotos,
  });

  const categories = photos
    ? ["all", ...Array.from(new Set(photos.map((p) => p.category.toLowerCase())))]
    : ["all"];

  const categoryLabel = (cat: string) => {
    if (cat === "all") return "Tout voir";
    return CATEGORY_LABELS[cat] || cat;
  };

  const filtered = photos?.filter((p) =>
    activeCategory === "all" || p.category.toLowerCase() === activeCategory
  ) ?? [];

  const closeLightbox = useCallback(() => setLightboxPhoto(null), []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-32 pb-20 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 animate-pulse">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-villa-offwhite rounded-2xl" style={{ aspectRatio: i % 3 === 0 ? "1/1.2" : "1/0.85" }} />
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ImageOff className="mx-auto h-12 w-12 text-villa-text-secondary/40 mb-4" />
            <p className="text-villa-text-secondary">Impossible de charger les photos</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Helmet>
        <title>Galerie photo — Villa Douce Vague au Grau du Roi</title>
        <meta name="description" content="Découvrez la Villa Douce Vague en images : intérieurs, chambres, piscine chauffée et extérieurs de cette location de vacances au Grau du Roi." />
        <meta property="og:title" content="Galerie photo — Villa Douce Vague" />
        <meta property="og:description" content="Photos de la Villa Douce Vague au Grau du Roi : piscine, chambres, intérieurs et jardin." />
        <meta property="og:url" content="https://www.villadoucevague.fr/galerie" />
      </Helmet>
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={closeLightbox}
        >
          <button
            aria-label="Fermer la galerie"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            onClick={closeLightbox}
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightboxPhoto.url}
            alt={lightboxPhoto.title}
            className="max-w-full max-h-[90vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Navbar />

      <main className="flex-1">
        {/* Page header */}
        <div className="pt-32 pb-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div
            ref={headerReveal.ref}
            className={`reveal ${headerReveal.revealed ? "in" : ""}`}
          >
            <p className="eyebrow mb-4">La Villa en Images</p>
            <h1 className="heading-lg text-villa-text mb-2">Galerie photo</h1>
            <p className="body-lg max-w-xl">
              Explorez chaque espace de la Villa Douce Vague et imaginez votre prochaine escapade au Grau du Roi.
            </p>
          </div>
        </div>

        {/* Category pills */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-villa-blue text-white"
                    : "bg-villa-offwhite text-villa-text/70 hover:text-villa-text hover:bg-villa-offwhite/80"
                }`}
              >
                {categoryLabel(cat)}
              </button>
            ))}
          </div>
        </div>

        {/* Masonry-style grid */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pb-24">
          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 space-y-3">
            {filtered.map((photo, i) => {
              const url = getPhotoUrl(photo.storage_path);
              const label = CATEGORY_LABELS[photo.category.toLowerCase()] || photo.category;
              return (
                <div
                  key={photo.id}
                  className="break-inside-avoid group relative overflow-hidden rounded-2xl cursor-zoom-in"
                  style={{
                    opacity: 0,
                    animation: `fadeUp 0.5s ease forwards`,
                    animationDelay: `${Math.min(i * 40, 400)}ms`,
                  }}
                  onClick={() => setLightboxPhoto({ url, title: photo.title || label })}
                >
                  <img
                    src={url}
                    alt={photo.title || `Villa Douce Vague — ${label}`}
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading={i < 8 ? "eager" : "lazy"}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <p className="text-white text-sm font-medium truncate">
                      {photo.title || label}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-24 text-villa-text-secondary">
              Aucune photo dans cette catégorie
            </div>
          )}
        </div>
      </main>

      <Footer />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Gallery;
